import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import TaskDetail from './TaskDetail';
import { useLocation, useNavigate } from 'react-router-dom';
import ConfirmModal from './ConfirmModal';
import TaskFormDrawer from './TaskFormDrawer';
import TasksListView from './TasksListView';
import useDebounce from '../hooks/useDebounce';
import {
  useTasks,
  useProjects,
  useTaskStatuses,
  useUsers,
  useCreateTask,
  useUpdateTask,
  useTaskStatistics,
  useDeletedTasks,
  useDeleteTask,
  useRestoreTask,
  useCreateTaskStatus,
  useDeleteTaskStatus,
} from '../hooks/api';

const EMPTY_FILTERS = {
  status: '',
  priority: '',
  assignee: '',
  project_id: '',
  tags: '',
  due_date_after: '',
  due_date_before: '',
  estimated_hours_min: '',
  estimated_hours_max: '',
  is_overdue: '',
  created_after: '',
  created_before: '',
  manual_timer_logging: '',
  github_integration: '',
};

const TasksPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedProject, setSelectedProject] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState({ name: '', color: 'bg-gray-100 text-gray-800' });
  const [availableMembers, setAvailableMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingStatus, setIsDeletingStatus] = useState(false);
  const [assignedUserInfo, setAssignedUserInfo] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(false);
  const [showDeletedTasks, setShowDeletedTasks] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [taskIdToRestore, setTaskIdToRestore] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Project leads manage the team list by default; "mine" shows only tasks assigned to them.
  const [taskScope, setTaskScope] = useState('all'); // 'all' | 'mine'
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const pageSizeOptions = [10, 15, 20, 50];
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskIdToDelete, setTaskIdToDelete] = useState(null);
  const [showDeleteStatusModal, setShowDeleteStatusModal] = useState(false);
  const [statusIdToDelete, setStatusIdToDelete] = useState(null);

  const urlQuery = new URLSearchParams(location.search);
  const filter = urlQuery.get('filter');
  const assignedUser = urlQuery.get('user');

  const debouncedSearch = useDebounce(searchTerm, 300);

  // Server-side query params — only the current page is fetched
  const taskQueryParams = useMemo(() => {
    const params = {
      page: currentPage,
      page_size: pageSize,
    };

    if (debouncedSearch) params.search = debouncedSearch;

    const projectId = selectedProject || filters.project_id;
    if (projectId) params.project_id = projectId;

    if (filters.status) params.status = filters.status;
    if (filters.priority) params.priority = filters.priority;
    if (filters.tags) params.tags = filters.tags;
    if (filters.due_date_after) params.due_date_after = filters.due_date_after;
    if (filters.due_date_before) params.due_date_before = filters.due_date_before;
    if (filters.is_overdue !== '') params.is_overdue = filters.is_overdue;
    if (filters.created_after) params.created_after = filters.created_after;
    if (filters.created_before) params.created_before = filters.created_before;
    if (filters.estimated_hours_min !== '') params.estimated_hours_min = filters.estimated_hours_min;
    if (filters.estimated_hours_max !== '') params.estimated_hours_max = filters.estimated_hours_max;
    if (filters.manual_timer_logging) params.manual_timer_logging = filters.manual_timer_logging;
    if (filters.github_integration) params.github_integration = filters.github_integration;

    // Assignee: URL ?filter=assigned&user=X takes precedence, then filter dropdown
    const assigneeId = filter === 'assigned' && assignedUser ? assignedUser : filters.assignee;
    if (assigneeId) params.assignee = assigneeId;

    if (user?.role === 'project_lead' && taskScope === 'mine') {
      params.scope = 'mine';
    }

    return params;
  }, [
    currentPage,
    pageSize,
    debouncedSearch,
    selectedProject,
    filters,
    filter,
    assignedUser,
    user?.role,
    taskScope,
  ]);

  const {
    data: tasksData,
    isLoading: tasksLoading,
    isFetching: tasksFetching,
    refetch: fetchTasks,
  } = useTasks(taskQueryParams);

  const { data: projectsData, isLoading: projectsLoading } = useProjects();
  const { data: statusesData, isLoading: statusesLoading } = useTaskStatuses();
  const { data: usersData, isLoading: usersLoading } = useUsers();

  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const deleteTaskMutation = useDeleteTask();
  const restoreTaskMutation = useRestoreTask();
  const createTaskStatusMutation = useCreateTaskStatus();
  const deleteTaskStatusMutation = useDeleteTaskStatus();

  const { data: taskStatisticsData } = useTaskStatistics();
  const { data: deletedTasksData, isLoading: deletedTasksLoading } = useDeletedTasks({
    enabled: user?.role === 'admin',
  });

  // API already returns the current page of filtered results
  const tasks = useMemo(() => tasksData?.results || [], [tasksData]);
  const serverTotal = tasksData?.total ?? 0;
  const projects = useMemo(() => projectsData?.results || [], [projectsData]);
  const taskStatuses = useMemo(() => statusesData || [], [statusesData]);
  const taskStatistics = taskStatisticsData || {};
  const deletedTasks = useMemo(() => deletedTasksData?.results || [], [deletedTasksData]);
  const usersList = useMemo(
    () => (Array.isArray(usersData) ? usersData : (usersData?.results || [])),
    [usersData]
  );

  const getFilterDescription = (filterType) => {
    switch (filterType) {
      case 'active-ontime':
        return 'Active & On-Time Tasks';
      case 'active-overdue':
        return 'Active & Overdue Tasks';
      case 'completed-ontime':
        return 'Completed On-Time Tasks';
      case 'completed-late':
        return 'Completed Late Tasks';
      case 'pending-ontime':
        return 'Pending & On-Time Tasks';
      case 'pending-overdue':
        return 'Pending & Overdue Tasks';
      case 'active':
        return 'Active Tasks';
      case 'overdue':
        return 'Overdue Tasks';
      case 'late':
        return 'Completed Late Tasks';
      default:
        return 'Filtered Tasks';
    }
  };

  const fetchAssignedUserInfo = async (userId) => {
    try {
      setLoadingUserInfo(true);
      const response = await axios.get(`/api/users/${userId}`);
      if (response.data && response.data.full_name) {
        setAssignedUserInfo(response.data);
      } else {
        setAssignedUserInfo({
          id: userId,
          full_name: response.data?.full_name || response.data?.name || 'Loading...',
          email: response.data?.email || 'Loading...',
        });
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      setAssignedUserInfo({
        id: userId,
        full_name: `User ${String(userId).slice(0, 8)}...`,
        email: 'User not found',
      });
    } finally {
      setLoadingUserInfo(false);
    }
  };

  useEffect(() => {
    if (assignedUser) fetchAssignedUserInfo(assignedUser);
    else setAssignedUserInfo(null);
  }, [assignedUser]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlFilter = params.get('filter');
    const status = params.get('status');
    const action = params.get('action');
    const assignee = params.get('assignee');

    if (action === 'create') {
      setEditingTask(null);
      setShowModal(true);
    }

    if (urlFilter === 'status' && status) {
      setFilters((prev) => ({ ...prev, status }));
      setCurrentPage(1);
    } else if (urlFilter === 'active') {
      setFilters((prev) => ({ ...prev, status: 'In Progress,To Do' }));
      setCurrentPage(1);
    } else if (urlFilter === 'overdue' || urlFilter === 'late' || urlFilter === 'completed_late') {
      setFilters((prev) => ({ ...prev, is_overdue: 'true' }));
      setCurrentPage(1);
    } else if (urlFilter === 'active-ontime') {
      setFilters((prev) => ({ ...prev, status: 'In Progress,To Do', is_overdue: 'false' }));
      setCurrentPage(1);
    } else if (urlFilter === 'active-overdue') {
      setFilters((prev) => ({ ...prev, status: 'In Progress,To Do', is_overdue: 'true' }));
      setCurrentPage(1);
    } else if (urlFilter === 'completed-ontime') {
      setFilters((prev) => ({ ...prev, status: 'Done,In QA,Completed', is_overdue: 'false' }));
      setCurrentPage(1);
    } else if (urlFilter === 'pending-ontime') {
      setFilters((prev) => ({ ...prev, status: 'To Do', is_overdue: 'false' }));
      setCurrentPage(1);
    } else if (urlFilter === 'pending-overdue') {
      setFilters((prev) => ({ ...prev, status: 'To Do', is_overdue: 'true' }));
      setCurrentPage(1);
    }

    if (assignee) {
      setFilters((prev) => ({ ...prev, assignee }));
      setCurrentPage(1);
    }
  }, [location.search]);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'project_lead') {
      axios
        .get('/api/team/active')
        .then((res) => {
          let members = res.data || [];
          if (user && !members.find((m) => m.id === user.id)) {
            members = [
              {
                id: user.id,
                username: user.username,
                full_name: user.full_name,
                email: user.email,
                role: user.role,
              },
              ...members,
            ];
          }
          setAvailableMembers(members);
        })
        .catch(() => {});
      return;
    }
    const list = Array.isArray(usersData) ? usersData : (usersData?.results || []);
    if (list.length) setAvailableMembers(list);
  }, [user, usersData]);

  // URL-driven filter changes should return to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [assignedUser, filter]);

  const handleTaskSubmit = async (taskData) => {
    if (editingTask) {
      await updateTaskMutation.mutateAsync({ id: editingTask.id, data: taskData });
      toast.success('Task updated successfully');
    } else {
      await createTaskMutation.mutateAsync(taskData);
      toast.success('Task created successfully');
    }
    await fetchTasks();
  };

  const handleDelete = (taskId) => {
    setTaskIdToDelete(taskId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTaskMutation.mutateAsync(taskIdToDelete);
      toast.success('Task deleted successfully');
      await fetchTasks();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete task');
    } finally {
      setShowDeleteModal(false);
      setTaskIdToDelete(null);
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setTaskIdToDelete(null);
  };

  const handleRestore = (taskId) => {
    setTaskIdToRestore(taskId);
    setShowRestoreModal(true);
  };

  const confirmRestore = async () => {
    setIsRestoring(true);
    try {
      await restoreTaskMutation.mutateAsync(taskIdToRestore);
      toast.success('Task restored successfully');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to restore task');
    } finally {
      setShowRestoreModal(false);
      setTaskIdToRestore(null);
      setIsRestoring(false);
    }
  };

  const cancelRestore = () => {
    setShowRestoreModal(false);
    setTaskIdToRestore(null);
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTask(null);
  };

  const getProjectName = (projectId) => {
    if (!Array.isArray(projects)) return 'Unknown Project';
    const project = projects.find((p) => p.id === projectId);
    if (!project) return 'Project Deleted';
    if (project.is_deleted) return `${project.name} (deleted)`;
    return project.name;
  };

  const handleViewTask = (taskId) => navigate(`/tasks/${taskId}`);

  const handleOpenTaskInNewWindow = (taskId) => {
    window.open(`${window.location.origin}/tasks/${taskId}`, '_blank', 'noopener,noreferrer');
  };

  const handleBackToList = () => setSelectedTaskId(null);

  const handleCreateStatus = async (e) => {
    e.preventDefault();
    if (!newStatus.name.trim()) return;
    try {
      await createTaskStatusMutation.mutateAsync(newStatus);
      setNewStatus({ name: '', color: 'bg-gray-100 text-gray-800' });
      setShowStatusModal(false);
      toast.success('Status created successfully');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create status');
    }
  };

  const confirmDeleteStatus = async () => {
    setIsDeletingStatus(true);
    try {
      await deleteTaskStatusMutation.mutateAsync(statusIdToDelete);
      toast.success('Status deleted successfully');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete status');
    } finally {
      setShowDeleteStatusModal(false);
      setStatusIdToDelete(null);
      setIsDeletingStatus(false);
    }
  };

  const getStatusColor = (statusName) => {
    if (statusName === 'In Progress') {
      return 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300';
    }
    const status = taskStatuses.find((s) => s.name === statusName);
    return status ? status.color : 'bg-secondary-100 text-secondary-700';
  };

  const handleFilterChange = useCallback((key, value) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setSelectedProject('');
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, []);

  const handleTaskScopeChange = useCallback((scope) => {
    setTaskScope(scope);
    setCurrentPage(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(serverTotal / pageSize));
  // Server already paginated — results are the current page
  const paginatedTasks = tasks;

  const membersForUi = availableMembers.length ? availableMembers : usersList;
  const canManageTask = (task) =>
    user?.role === 'admin' || (user?.role === 'project_lead' && task.created_by === user?.id);

  // Full-page spinner only on first load (keep previous page while refetching)
  const isInitialLoading =
    (tasksLoading && !tasksData) || projectsLoading || statusesLoading || usersLoading;

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const filterBanner =
    filter === 'assigned' && assignedUser ? (
      <div className="flex items-center gap-2 mt-1">
        <p className="text-sm text-primary-600 font-medium">
          Filtered: Tasks assigned to{' '}
          {loadingUserInfo
            ? 'Loading...'
            : assignedUserInfo
              ? assignedUserInfo.full_name || assignedUserInfo.email
              : 'Loading...'}
        </p>
        <button
          type="button"
          onClick={() => navigate('/tasks')}
          className="text-xs text-secondary-500 hover:text-secondary-700 underline"
        >
          Clear filter
        </button>
      </div>
    ) : filter && filter !== 'assigned' ? (
      <p className="text-sm text-secondary-500 mt-1">{getFilterDescription(filter)}</p>
    ) : null;

  return (
    <>
      {selectedTaskId ? (
        <TaskDetail taskId={selectedTaskId} onBack={handleBackToList} />
      ) : (
        <TasksListView
          user={user}
          title={
            user?.role === 'member'
              ? 'My Assigned Tasks'
              : user?.role === 'project_lead' && taskScope === 'mine'
                ? 'My Tasks'
                : 'Tasks'
          }
          subtitle={
            user?.role === 'project_lead' && taskScope === 'mine'
              ? 'Tasks assigned to you'
              : 'Showing all tasks including subtasks'
          }
          taskScope={taskScope}
          onTaskScopeChange={handleTaskScopeChange}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters((v) => !v)}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          taskStatuses={taskStatuses}
          availableMembers={membersForUi}
          projects={projects}
          filteredTasks={paginatedTasks}
          paginatedTasks={paginatedTasks}
          serverTotal={serverTotal}
          isFetching={tasksFetching}
          statistics={taskStatistics}
          currentPage={currentPage}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          onCreate={() => {
            setEditingTask(null);
            setShowModal(true);
          }}
          onView={handleViewTask}
          onOpenInNewWindow={handleOpenTaskInNewWindow}
          onEdit={handleEdit}
          onDelete={handleDelete}
          showDeletedTasks={showDeletedTasks}
          onToggleDeleted={() => setShowDeletedTasks((v) => !v)}
          deletedTasksCount={taskStatistics?.deleted_tasks || 0}
          deletedTasksLoading={deletedTasksLoading}
          deletedTasks={deletedTasks}
          onRestore={handleRestore}
          canManageTask={canManageTask}
          getProjectName={getProjectName}
          getStatusColor={getStatusColor}
          filterBanner={filterBanner}
        />
      )}

      <TaskFormDrawer
        open={showModal}
        onClose={handleCloseModal}
        editingTask={editingTask}
        currentUser={user}
        onSubmit={handleTaskSubmit}
        initialProjectId={selectedProject || filters.project_id || ''}
      />

      {showStatusModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowStatusModal(false)}
        >
          <div
            className="bg-white dark:bg-secondary-800 rounded-xl p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Create New Status</h3>
            <form onSubmit={handleCreateStatus} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Status Name *</label>
                <input
                  type="text"
                  className="input-field"
                  value={newStatus.name}
                  onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
                  placeholder="e.g., In Review, Testing"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Color</label>
                <select
                  className="input-field"
                  value={newStatus.color}
                  onChange={(e) => setNewStatus({ ...newStatus, color: e.target.value })}
                >
                  <option value="bg-gray-100 text-gray-800">Gray</option>
                  <option value="bg-blue-100 text-blue-800">Blue</option>
                  <option value="bg-green-100 text-green-800">Green</option>
                  <option value="bg-yellow-100 text-yellow-800">Yellow</option>
                  <option value="bg-orange-100 text-orange-800">Orange</option>
                  <option value="bg-red-100 text-red-800">Red</option>
                  <option value="bg-purple-100 text-purple-800">Purple</option>
                </select>
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setShowStatusModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Status
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={showDeleteModal}
        title="Delete Task"
        message="Are you sure you want to delete this task? This will also delete all associated subtasks."
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />

      <ConfirmModal
        open={showDeleteStatusModal}
        title="Delete Status"
        message="Are you sure you want to delete this status? This action cannot be undone."
        onConfirm={confirmDeleteStatus}
        onCancel={() => {
          setShowDeleteStatusModal(false);
          setStatusIdToDelete(null);
        }}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeletingStatus}
      />

      <ConfirmModal
        open={showRestoreModal}
        title="Restore Task"
        message="Are you sure you want to restore this task?"
        onConfirm={confirmRestore}
        onCancel={cancelRestore}
        confirmText="Restore"
        cancelText="Cancel"
        isLoading={isRestoring}
      />
    </>
  );
};

export default TasksPage;
